import { Utils } from '../../../../../utils/common'
import { logger } from '../../../../../utils/common/logger'
import { DOMEvents, DOMUtils } from '../../../../../utils/dom'
import { Store } from '../../../../../utils/storage/keys'
import { LocalStore } from '../../../../../utils/storage/local'
import { AnalyticsService } from '../../../../analytics'
import { IdentityEvent } from '../../../../common/events/identity'
import { IProfile } from '../../../../common/profile'
import SailthruAdapter from '../../sailthru'
import { LoginRadiusAdapter, LRErrorCode } from '../index'
import { ISuccessResponse } from '../widgets/login-widget'
import { LoginRadiusApiClient } from './api-client'

export interface IWindow extends Window {
    Sailthru: any
    tp: any
}

declare let window: IWindow

export enum PostRegistrationActions {
    REDIRECT = 'Redirect',
    SUBSCRIBE = 'Subscribe',
    SHOW_CHECKOUT = 'Show Checkout',
    SHOW_NEWSLETTER_OPT_IN = 'Show Newsletter Opt-In',
    SHOW_SUCCESS_SCREEN = 'Show Success Screen',
}

export interface ILRApiClientConfig {
    apiKey: string
    appName: string
    integrationName: string
    objectName: string
}

export class LoginService {
    // Mobile app expects this parameter to contain the auth token
    // and be passed in the to-mobile-app redirection from the device's browser.
    public static readonly mobileRedirectTokenName = 'token'
    // Mobile application is expected to provide this parameter in the URL
    // when opening sign-in and registration pages.
    // If the param is present then we know we need to redirect there after
    // successful authentication.
    private static readonly mobileRedirectParamName = 'redirect_uri'
    private static readonly redirectParamName = 'redirect'

    private static readonly allowedMobileRedirects = [
        'calgaryherald://',
        'nationalpost://',
        'financialpost://',
        'edmontonjournal://',
        'vancouversun://',
        'londonfreepress://',
        'ottawacitizen://',
        'windsorstar://',
        'montrealgazette://',
        'leaderpost://',
        'starphoenix://',
        'canoe://',
        'edmontonsun://',
        'calgarysun://',
        'torontosun://',
        'theprovince://',
        'ottawasun://',
        'winnipegsun://',
        'com.PostMedia.calgaryherald',
        'com.indusblue.calgaryherald',
        'com.nationalpost.nationalpostipad',
        'com.nationalpost',
        'com.financialpost',
        'com.PostMedia.edmontonjournal',
        'com.indusblue.edmontonjournal',
        'com.PostMedia.vancouversun',
        'com.indusblue.vancouversun',
        'com.fivemobile.canoe.newsreader.londonfreepress',
        'com.nurun.londonfreepress',
        'com.postmedia.ottawacitizen.ios-2',
        'com.postmedia.ottawacitizen.android2',
        'com.PostMedia.windsorstar',
        'com.indusblue.windsorstar',
        'com.PostMedia.montrealgazette',
        'com.indusblue.montrealgazette',
        'com.PostMedia.leaderpost',
        'com.indusblue.leaderpost',
        'com.PostMedia.starphoenix',
        'com.indusblue.starphoenix',
        'com.halifax.saltwire',
        'com.whiz.saltwire',
        'com.canoe.fivemobile',
        'com.nurun.canoe',
        'com.calgarysun',
        'com.nurun.calgarysun',
        'com.winnipegsun',
        'com.nurun.winnipegsun',
        'com.torontosun',
        'com.nurun.torontosun',
        'com.PostMedia.theprovince',
        'com.indusblue.theprovince',
        'com.edmontonsun',
        'com.nurun.edmontonsun',
        'com.ottawasun',
        'com.nurun.ottawasun',
    ]

    public static getLRApiClient(config: ILRApiClientConfig, token: string) {
        return new LoginRadiusApiClient(
            config.apiKey,
            config.integrationName,
            token,
            config.appName,
            config.objectName
        )
    }
    public static setMobileRedirectUrl() {
        if (sessionStorage) {
            const redirectUri = LoginService.getParamFromQueryString(LoginService.mobileRedirectParamName)
            if (LoginService.isMobileRedirect(redirectUri)) {
                sessionStorage.setItem(Store.MOBILE_REDIRECT_URI, redirectUri)
            }
        }
    }

    public static setRedirectUrl(url: string) {
        if (sessionStorage) {
            sessionStorage.setItem(Store.REDIRECT_URL, url)
        }
    }

    public static isMobileRedirect(url: string) {
        const mobile = (redirect: string) =>
            url.toLowerCase().startsWith(redirect.toLowerCase()) // Case insensitive
        return url && LoginService.allowedMobileRedirects.some(mobile)
    }

    public static getMobileRedirectUrl() {
        let redirectUrl

        if (sessionStorage) {
            const url = sessionStorage.getItem(Store.MOBILE_REDIRECT_URI)
            if (LoginService.isMobileRedirect(url)) {
                redirectUrl = url
            }
        }

        return redirectUrl
    }

    public static getDesktopRedirectUrl() {
        let redirectUrl
        if (sessionStorage) {
            redirectUrl = sessionStorage.getItem(Store.REDIRECT_URL)
        }
        return redirectUrl
    }

    /**
     * Get redirect url.
     *
     * The URL returned by this function should be used to do the actual redirection.
     *
     * To get a URL that can be passed as a callback to LR please see the buildRedirectUrl method below.
     *
     * Mobile redirect is intended to redirect the user to the mobile app and to provide
     * the auth token to the app. Because of that, if token is available, we append it
     * to the mobile redirection.
     *
     * @param defaultRedirect - if neither mobile nor traditional redirect is set then use this one
     * @param token - auth token to be appended to the mobile redirect
     * @returns
     */
    public static getRedirectUrl(defaultRedirect: string, token?: string) {
        let redirectUrl = (
            LoginService.getRedirectURLFromLocation() ||
            LoginService.getMobileRedirectUrl() ||
            LoginService.getDesktopRedirectUrl() ||
            defaultRedirect
        )

        if (redirectUrl) {
            if (!redirectUrl.match(/^[a-zA-Z0-9-\.]+:\/\//)) {
                redirectUrl = `${window.location.protocol}//${redirectUrl}`
            }

            const redirectHostname = new URL(redirectUrl).hostname
            const isMobile = LoginService.isMobileRedirect(redirectUrl)

            if (!isMobile && (window.location.hostname !== redirectHostname)) {
                // redirect url domain does not match with site domain
                redirectUrl = window.location.origin
            } else if(isMobile && token) {
                // On mobile devices we redirect from the device's browser, where
                // user has authenticated, to the mobile application.
                // We use so-called deep link for that and in this case we need to
                // pass the auth token as the redirection param.
                try {
                    redirectUrl = LoginService.addMobileRedirectTokenToUrl(redirectUrl, token)
                } catch (e) {
                    logger('error', 'Error adding token to the mobile redirect link.', e)
                }
            }
        }

        return redirectUrl
    }

    /**
     * Build post auth redirect URL.
     *
     * The URL generated by this function is intended to be passed as a redirection parameter
     * when we call LR. So that after a user comes back from LR we can redirect them to the URL.
     *
     * To get a URL that can be used for doing the actual redirect please see the getRedirectUrl method above.
     *
     * If set then the mobile redirect takes precedence over the traditional redirect.
     *
     * @param defaultRedirect - if neither mobile nor traditional redirect is set then use this one
     * @returns
     */
    public static buildRedirectUrl(defaultRedirect: string) {
        return (
            LoginService.getMobileRedirectUrl() ||
            LoginService.getDesktopRedirectUrl() ||
            defaultRedirect
        )
    }

    public static getSSORedirectURL(token: string, redirectUrl: string) {
        const ssoRedirectURL = new URL(LoginRadiusAdapter.ssoRedirectLink)
        const callbackURL = redirectUrl ? redirectUrl : location.origin
        ssoRedirectURL.searchParams.append('token', token)
        ssoRedirectURL.searchParams.append('callback', callbackURL)
        return ssoRedirectURL.toString()
    }

    public static handleLogin(response: ISuccessResponse) {
        window.tp = window.tp || []
        const jwtToken = response.jwttoken

        if (jwtToken) {
             // store token
            localStorage.setItem(Store.JWT, jwtToken)

            window.tp.push(['init', () => {
                // Setting the new JWT token for authenticated user
                window.tp.push(['setExternalJWT', jwtToken])
            }])
        }
    }

    public static async setSailthruCookie(email: string) {
        try {
            await SailthruAdapter.setCookie(email)
        } catch (e) {
            logger('error', e)
        }
    }

    /**
     * Determine whether this is a first sign-in of the user.
     *
     * In case of social login this means the user has just registered.
     * @param data LR onSuccess callback data
     */
    public static isRegistration(data: {NoOfLogins: number}) {
        return data.NoOfLogins === 1
    }

    /**
     * Return newsletter opt-in page URL.
     *
     * @param baseURL
     */
    public static getNewsletterRedirectURL(baseURL: string) {
        return `${baseURL}/newsletter-opt-in/`
    }

    /**
     * Return registration success page URL.
     *
     * @param baseURL
     */
    public static getRegistrationSuccessURL(baseURL: string) {
        return `${baseURL}/registration-success/`
    }

    /**
     * Get piano params from session storage.
     *
     * If params are present then it means the user is in the subscribe funnel.
     */
    public static getPianoParams() {
        return window.sessionStorage ? sessionStorage.getItem(Store.PIANO_PARAMS) : undefined
    }

    /**
     * Show Piano checkout modal
     * @param pianoParams
     */
    public static showCheckoutModal(pianoParams: any) {
        // If params object is valid and the user is logged in - start checkout
        window.tp.push(['init', () => {
            window.tp.offer.startCheckout(JSON.parse(pianoParams))
        }])
    }

    /**
     * Check if there's an activation in progress
     */
    public static isActivation() {
        if (window.sessionStorage && sessionStorage.getItem(Store.ACTIVATE_BUTTON)) {
            sessionStorage.removeItem(Store.ACTIVATE_BUTTON)
            return true
        }
        return false
    }

    /**
     * Check if there's a subscription in progress
     */
    public static isSubscription() {
        if (
            window.sessionStorage && sessionStorage.getItem(Store.USER_WANTS) === 'subscribe'
        ) {
            sessionStorage.removeItem(Store.USER_WANTS)
            return true
        }
        return false
    }

    /**
     * Check if there's a checkout in progress
     */
    public static isCheckout() {
        return !!LoginService.getPianoParams()
    }

    /**
     * Check if coming from home delivery form
     */
    public static isHomeDelivery(redirectUrl: string) {
        return redirectUrl.includes('/home-delivery/')
    }

    /**
     * What to do after registration
     */
    public static getPostRegistrationStep(redirectUrl: string, isNewsletterEnabled: boolean) {
        const isActivation = LoginService.isActivation()
        const isHomeDelivery = LoginService.isHomeDelivery(redirectUrl)
        const isSubscription = LoginService.isSubscription()
        const isCheckout = LoginService.isCheckout()
        const isMobileRedirect = LoginService.isMobileRedirect(redirectUrl)
        let action = PostRegistrationActions.SHOW_SUCCESS_SCREEN

        if (redirectUrl) {
            if (isActivation || isHomeDelivery) {
                action = PostRegistrationActions.REDIRECT
            } else if (isMobileRedirect && !isSubscription) {
                // Skip registration success screen if it's a redirect to the mobile app
                action = PostRegistrationActions.REDIRECT
            } else if (isSubscription) {
                // Skip registration success screen if this is checkout
                if (isCheckout) {
                    action = PostRegistrationActions.SHOW_CHECKOUT
                } else {
                    action = PostRegistrationActions.SUBSCRIBE
                }
            } else if (isNewsletterEnabled) {
                // Skip registration success screen if it's a redirect to the newsletter opt-in page
                action = PostRegistrationActions.SHOW_NEWSLETTER_OPT_IN
            }
        }
        return action
    }

    public static async handleLoginSuccess(
        response: ISuccessResponse, isSocialSignIn = false, showNewsletterOptIn = false
    ) {
        const token = response.access_token
         // LR returns Data or Profile in the login success response object.
        const data = response.Data || response.Profile

        LoginService.handleLogin(response)
        // Get stored redirect URL from where user navigated to the sign in page
        let redirectUrl = LoginService.getRedirectUrl('', token)
        const profileData = LoginService.createProfile(data)
        let mpLoginFinishWaitTimeout: number
        let hasCbFinalizeLoginCalled = false

        const waitAndRedirectUrl = (urlRedirectWaitTimeInMilliSec: number) => {
            // Wait before leaving the page to ensure analytics events are sent
            logger('info', 'waiting for mParticle to send login event')
            window.setTimeout(() => {
                logger('info', 'finalizing login')
                window.location.replace(LoginService.getSSORedirectURL(token, redirectUrl))
            }, urlRedirectWaitTimeInMilliSec)
        }

        // Store token to local storage
        localStorage.setItem(Store.LR_TOKEN_KEY, token)

        /**
         * The cbFinalizeLogin function may be called:
         * - directly
         * - by an event handler
         * - by a timeout that executes in case the event handler is not called
         *
         * Because of the above we need to clear the timeout as soon
         * as the function is called to prevent it from being
         * called again by the timeout.
         */
        const cbFinalizeLogin = async (urlRedirectWaitTimeInMilliSec: number) => {
            // Make sure the timeout is cleared
            clearTimeout(mpLoginFinishWaitTimeout)

            if (hasCbFinalizeLoginCalled) {
                return
            }

            hasCbFinalizeLoginCalled = true

            await LoginService.setSailthruCookie(profileData.email)

            let signInMethod = response.Profile.RegistrationProvider
            if (signInMethod === 'live') {
                // change sign in method name to microsoft
                signInMethod = 'microsoft'
            }

            const pianoParams = LoginService.getPianoParams()

            if (!LoginService.isRegistration(response.Profile) && LoginService.isActivation()) {
                const profile = LocalStore.get<IProfile>(Store.ID)
                const printSubscriber = profile.printSubscriber

                if (printSubscriber) {
                    // Set redirect to already activated page
                    redirectUrl = `${window.location.origin}/already-activated/`
                }

                waitAndRedirectUrl(urlRedirectWaitTimeInMilliSec)
                return
            }

            if (LoginService.isRegistration(response.Profile)) {

                const registeredPlatform = response.Profile?.CustomFields?.register_platform
                // First time Social Login will miss the custom field `register_platform`.
                // If the custom field missed for just registerd profile,
                // set the value via LR Update Profile By Token api
                if (!registeredPlatform) {
                    const lrTokenKey = localStorage.getItem(Store.LR_TOKEN_KEY)
                    const pageData = JSON.parse(document.querySelector('#page-data')?.innerHTML || '{}')
                    const apiKey = pageData?.identity?.sso?.loginradius?.apiKey || ''

                    const mobileRedirectUrl = LoginService.getMobileRedirectUrl()
                    const platform = mobileRedirectUrl ? 'Unified' : pageData?.analytics?.platform

                    const loginRadiusApiClient = new LoginRadiusApiClient(
                        apiKey,
                        undefined,
                        lrTokenKey,
                        undefined,
                    )
                    try {
                        await loginRadiusApiClient.updateProfileByToken({
                            CustomFields: {
                                register_platform: platform
                            }
                        })
                    } catch (e) {
                        logger('error', 'Registered Platform details couldn\'t update in profile.' ,e)
                    }
                }
                // Add user to Sailthru Social Registration List if UTM Reg Source is social
                await LoginService.addUserToSocialRegList(profileData.email)
                LoginService.sendRegistrationAnalytics(signInMethod || 'traditionalRegister')
                const action = LoginService.getPostRegistrationStep(redirectUrl, showNewsletterOptIn)
                if (action === PostRegistrationActions.SHOW_NEWSLETTER_OPT_IN) {
                   redirectUrl = LoginService.getNewsletterRedirectURL(location.origin)
                   waitAndRedirectUrl(urlRedirectWaitTimeInMilliSec)
                   return
                }
            } else {
                LoginService.sendLoginAnalytics(signInMethod || 'traditionalSignin')
            }

            // show checkout
            if (pianoParams) {
                // If params object is valid and the user is logged in - start checkout
                LoginService.showCheckoutModal(pianoParams)
            } else {
                waitAndRedirectUrl(urlRedirectWaitTimeInMilliSec)
            }
        }

        const fireIdentityEvents = () => {
            document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {
                ...profileData,
                isLogin: true
            }))

            // wait until profile get updated
            document.addEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, () => {
                document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_LOGIN, {
                    detail: {
                        email: profileData.email,
                        uuid: profileData.uuid
                    }
                }))
            })
        }

        if (!isSocialSignIn || this.isAppleRegistration()) {
            document.addEventListener(IdentityEvent.MP_LOGIN_FINISHED, async () => {
                // finalize, wait 600ms & redirect
                // need more time than social signin to send user_alias, form_interaction, signin and page_scroll events
                const urlRedirectWaitTimeInMilliSec = 600
                await cbFinalizeLogin(urlRedirectWaitTimeInMilliSec)
            })
            fireIdentityEvents()
        } else {
            // Already logged in. No need to wait for profile to initialize
            document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_LOGIN, {
                detail: {
                    email: profileData.email,
                    uuid: profileData.uuid
                }
            }))

            document.addEventListener(IdentityEvent.MP_LOGIN_FINISHED, async () => {
                // Finalize, wait & redirect
                // longer wait time for Safari, so that it can record the sign in event in mParticle
                const urlRedirectWaitTimeInMilliSec =
                    (DOMUtils.browserType() === 'Safari') ? 1000 : 600 // tslint:disable-line: no-magic-numbers
                await cbFinalizeLogin(urlRedirectWaitTimeInMilliSec)
            })
        }

        // In case IdentityEvent.MP_LOGIN_FINISHED is not triggered, we need to finalize the login
        // after a certain amount of time.
        const mpLoginFinishWaitTimeoutInMilliSec = 5000
        mpLoginFinishWaitTimeout = window.setTimeout(async () => {
            const urlRedirectWaitTimeInMilliSec = 600
            await cbFinalizeLogin(urlRedirectWaitTimeInMilliSec)
        }, mpLoginFinishWaitTimeoutInMilliSec)
    }

    /**
     * Clear error messages
     */
    public static clearErrors() {
        const errorMsg = document.querySelector(`.${LoginRadiusAdapter.DOM.class.errorMessageContainer}`)
        if (errorMsg) {
            errorMsg.remove()
        }

        // clear input errors
        const inputErrorWrappers = document.querySelectorAll(`.${LoginRadiusAdapter.DOM.class.inputWrapperError}`)

        inputErrorWrappers.forEach((inputErrorWrapper) => {
            const inputField = inputErrorWrapper.querySelector('input')
            const inputError = inputErrorWrapper.querySelector(`.${LoginRadiusAdapter.DOM.class.inputErrorMessage}`)

            DOMUtils.removeClass(inputErrorWrapper, `${LoginRadiusAdapter.DOM.class.inputWrapperError}`)

            if (inputField) {
                inputField.removeAttribute('aria-describedby')
                inputField.removeAttribute('aria-invalid')
            }

            if (inputError) {
                inputError.remove()
            }
        })
    }

    /**
     * Display any errors that appear after submitting the form
     * @param errors any
     */
    public static showErrors(errors: any) {
        if (Array.isArray(errors)) {
            const isApiErrorArray = !!(errors.length && errors[0].ErrorCode)

            if (isApiErrorArray) {
                const errorDiv = document.createElement('div')
                const errorList = document.createElement('ul')
                DOMUtils.addClass(errorList, 'errorlist')

                // Show api errors
                const identityScreenForm = document.querySelector(`.${LoginRadiusAdapter.DOM.class.identityForm}`)
                errorDiv.setAttribute('role', 'alert')
                errorDiv.className = LoginRadiusAdapter.DOM.class.errorMessageContainer

                const error = errors[0]
                const errorListItem = document.createElement('li')

                if (error.Description) {
                    if (error.ErrorCode === LRErrorCode.VERIFY_EMAIL_FIRST) {
                        // Verify email address social sign in error
                        const errorListSpan = document.createElement('span')
                        const errorLink = document.createElement('a')

                        errorLink.className = 'identity-verify-email'
                        errorLink.href = '/verify-email/'
                        errorLink.innerText = 'Resend Verify Email?'
                        errorLink.onclick = () => {
                            LoginService.sendClickEvent(
                                errorLink.innerText,
                                'link',
                                'social-sign-in',
                                errorLink.href,
                            )
                        }

                        errorListSpan.innerHTML = `${error.Description} `
                        errorListSpan.appendChild(errorLink)

                        errorListItem.appendChild(errorListSpan)
                    } else {
                        errorListItem.innerHTML = error.Description
                    }

                    errorList.appendChild(errorListItem)
                }

                if (identityScreenForm) {
                    errorDiv.appendChild(errorList)
                    identityScreenForm.after(errorDiv)
                    // If there are content blocker errors then scroll the view down to the blocker
                    this.scrollToContentBlocker()
                }
            } else {
                // Show input errors
                errors.map((error) => {
                    if (error.Id && error.Message) {
                        const inputField = document.querySelector(`#${error.Id}`)
                        const inputErrorSpan = document.createElement('span')
                        const inputErrorList = document.createElement('ul')
                        const errorListItem = document.createElement('li')
                        const errorMessageId = `${error.Id}__error`
                        let ariaDescribedBy = inputField.getAttribute('aria-describedBy')

                        if (!ariaDescribedBy) {
                            ariaDescribedBy = `${errorMessageId}`
                        }

                        inputField.setAttribute('aria-describedBy', ariaDescribedBy)
                        inputField.setAttribute('aria-invalid', 'true')
                        inputErrorSpan.id = errorMessageId
                        errorListItem.innerHTML = error.Message

                        // Error field styling classes
                        DOMUtils.addClass(inputErrorSpan, LoginRadiusAdapter.DOM.class.inputErrorMessage)
                        DOMUtils.addClass(inputErrorList, 'errorlist')

                        // Add validation error
                        const inputWrapper = inputField.parentElement
                        DOMUtils.addClass(inputWrapper, LoginRadiusAdapter.DOM.class.inputWrapperError)
                        inputErrorList.appendChild(errorListItem)
                        inputErrorSpan.appendChild(inputErrorList)
                        inputWrapper.appendChild(inputErrorSpan)
                    }
                })

                // tslint:disable-next-line: max-line-length
                const firstInputError = document.querySelector(`.${LoginRadiusAdapter.DOM.class.inputWrapperError} input`) as HTMLInputElement

                if (firstInputError) {
                    firstInputError.focus()
                }
            }
        }
    }

    /**
     * Takes user to the content blocker
     */
    public static scrollToContentBlocker() {
        const contentBlocker = document.querySelector(`.${LoginRadiusAdapter.DOM.id.contentBlocker}`)
        if (contentBlocker) {
            // Scroll immediately if the blocker is already visible.
            contentBlocker.scrollIntoView({block: 'nearest'})
            // Scroll later, using observer - for situations when blocker is shown later by Piano scripts.
            const observer = new MutationObserver((_mutations) => {
                // What Piano Composer does is a removal of the `hidden` class
                if (!contentBlocker.classList.contains('hidden')) {
                    contentBlocker.scrollIntoView({block: 'nearest'})
                }
            })
            observer.observe(contentBlocker, {attributes: true, attributeFilter:['class']})
        }
    }

    /**
     * Set the back to site link to the redirect url in session storage
     * Redirects to hosted solution if there is a token
     *
     * @param {string} token
     */
    public static setRedirectLink(token?: string) {
        const backToSiteButton = document.querySelector('.button__back-to-site') as HTMLAnchorElement

        let redirectUrl

        if (sessionStorage) {
            if (token) {
                const callbackUrl = LoginService.getRedirectUrl(window.location.origin, token)
                redirectUrl = LoginService.getSSORedirectURL(token, callbackUrl)
            } else {
                redirectUrl = LoginService.getRedirectUrl('')
            }
        }

        if (backToSiteButton && redirectUrl) {
            backToSiteButton.href = redirectUrl
        }
    }

    /**
     * Remove the access token parameter from the URL
     */
    public static removeAccessTokenFromUrl() {
        history.replaceState(history.state, '', DOMUtils.removeFromUrl('lr-token', location.href))
    }

    /**
     * Send login analytics
     * @param {string} signInMethod: 'social' or 'traditionalSignin'
     */
    public static sendLoginAnalytics(signInMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            sign_in_method: 'sign_in_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signin,
                integration_name: 'LoginRadius',
                sign_in_method: signInMethod
            }
        }))
    }

    /**
     * Send registration analytics event
     * @param {string} registrationMethod  'social' or 'traditionalSignin'
     * @param {any} profileData user profile data
     */
    public static sendRegistrationAnalytics(registrationMethod: string) {
        const abTestName = (document.cookie.split(';').find((c) =>
            c.trim().startsWith(`${Store.SOPHI_AB_TEST_NAME}=`)) || '').trim().split('=').pop() || undefined
        const abTestAssignedGroup = (document.cookie.split(';').find((c) =>
            c.trim().startsWith(`${Store.SOPHI_AB_TEST}=`)) || '').trim().split('=').pop() || undefined
        const variantName = (abTestName && abTestAssignedGroup) ?
            `${abTestName}:${abTestAssignedGroup}` : undefined

        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            registration_method: 'registration_method',
                            variant_name: 'variant_name',
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.registration,
                integration_name: 'LoginRadius',
                registration_method: registrationMethod,
                variant_name: variantName
            }
        }))
    }

    /**
     * Send signout analytics
     */
    public static sendLogoutAnalytics() {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signout,
                integration_name: 'LoginRadius',
            }
        }))
    }

    /**
     * Send submission error analytics on form submission
     *
     * @param string errors
     * @param string formName
     */
    public static sendSubmissionErrorAnalytics(errors: any, formName: string) {
        if (Array.isArray(errors) && errors.length) {
            // API error
            if (errors[0].ErrorCode) {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    error_code: 'error_code',
                                    error_message: 'error_message',
                                    form_name: 'form_name',
                                },
                                mp_event_type: 'UserContent'
                            }
                        },
                        error_code: `${errors[0].ErrorCode}`,
                        error_message: errors[0].Description,
                        event_name: AnalyticsService.customEventNames.submissionError,
                        form_name: formName,
                    }
                }))
            } else {
                // input field error
                errors.map((error) => {
                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        error_message: 'error_message',
                                        form_name: 'form_name',
                                        input_id: 'input_id',
                                    },
                                    mp_event_type: 'UserContent'
                                }
                            },
                            error_message: error.Message,
                            event_name: AnalyticsService.customEventNames.submissionError,
                            form_name: formName,
                            input_id: error.Id,
                        }
                    }))
                })
            }
        }
    }

    /**
     * Send form submission analytics event
     *
     * @param string formName
     */
    public static sendFormSubmissionEvent(formName: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            form_name: 'form_name',
                        },
                        mp_event_type: 'Other'
                    }
                },
                event_name: AnalyticsService.customEventNames.formSubmission,
                form_name: formName,
            }
        }))
    }

    /**
     * Send form interaction event
     *
     * @param string formWrapper
     * @param string formName
     */
    public static sendFormInteractionEvent(formWrapper: string, formName: string) {
        const form = document.querySelector(`#${formWrapper} form`)

        if (form) {
            form.addEventListener(DOMEvents.FOCUS_IN, () => {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    form_name: 'form_name',
                                },
                                mp_event_type: 'Other'
                            }
                        },
                        event_name: AnalyticsService.customEventNames.formInteraction,
                        form_name: formName,
                    }
                }))
            }, {once: true})
        }
    }

    /**
     * Send click event
     *
     * @param string anchorText
     * @param string clickSourceType
     * @param string layoutSection
     * @param string targetUrl
     */
    public static sendClickEvent(anchorText: string, clickSourceType: string,
                                 layoutSection: string, targetUrl: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                action: DOMEvents.click,
                anchor_text: anchorText,
                click_source_type: clickSourceType,
                control_fields: {
                    mparticle: {
                        keys: {
                            anchor_text: 'anchor_text',
                            click_source_type: 'click_source_type',
                            layout_section: 'layout_section',
                            target_url: 'target_url'
                        },
                        mp_event_type: 'Navigation'
                    }
                },
                event_name: AnalyticsService.customEventNames.click,
                layout_section: layoutSection,
                target_url: targetUrl
            }
        }))
    }

    /**
     * Add User to social registration List
     */
    public static async addUserToSocialRegList(email: string) {
        const hasCookie = document.cookie.split('; ').some((c) => c.startsWith('utm_reg=social'))
        if (hasCookie) {
            try {
                // delete the cookie after use
                document.cookie = 'utm_reg=social; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
                // send to sailthru
                await SailthruAdapter.addToEmailList(email, SailthruAdapter.socialRegListId)
            } catch (e) {
                logger('error', 'Error adding user to Sailthru social registration list.', e)
            }
        }
    }

    /**
     * Shapes the LoginRadius response into a profile object
     */
    public static createProfile(data: any) {
        return {
            created: data.CreatedDate,
            doNotContact: undefined,
            email: (Array.isArray(data.Email)) ? data.Email[0].Value : '',
            emailConfirmed: data.EmailVerified,
            familyName: data.LastName,
            gender: data.Gender,
            givenName: data.FirstName,
            lastSignIn: data.LastLoginDate,
            name: data.FullName,
            uuid: data.Uid
        } as IProfile
    }

    /**
     * Change footer links to redirect to hosted solution
     *
     * @param {string} token
     */
     public static setSSOFooterLinks(token: string) {
        document.querySelectorAll('.identity-footer a').forEach((link: HTMLAnchorElement) => {
            let redirectUrl
            if (link.href.indexOf('http') !== -1) {
                redirectUrl = link.href
            } else {
                redirectUrl = `${window.location.origin}${link.href}`
            }
            link.href = LoginService.getSSORedirectURL(token, redirectUrl)
        })
    }

    public static isAppleRegistration(): boolean {
        return document.getElementById('loginradius-submit-login') !== null &&
            document.getElementById('loginradius-submit-register') !== null ||
             document.querySelectorAll('#loginradius-submit-login').length > 1
    }

    /**
     * Sets session storage keys
     *
     * @param urlParams
     */
    public static setSessionStorage(urlParams: URLSearchParams) {
        const sessionStorageKeys = [
            Store.CAMPAIGN,
            Store.CAMPAIGN_NAME,
            Store.OFFER_GROUP,
            Store.PIANO_PARAMS
        ] // url params that we want to add to session storage

        sessionStorageKeys.map((key) => {
            if (urlParams.has(key)) {
                sessionStorage.setItem(key, urlParams.get(key))
            }
        })

        // set redirect url session storage key
        if (urlParams.has(Store.PIANO_PARAMS) && urlParams.get(LoginService.redirectParamName)) {
            LoginService.setRedirectUrl(urlParams.get(LoginService.redirectParamName))
        }
    }

    private static addMobileRedirectTokenToUrl(url: string, token: string) {
        const urlObj = new URL(url)
        urlObj.searchParams.append(LoginService.mobileRedirectTokenName, token)
        return urlObj.toString()
    }

    private static getRedirectURLFromLocation() {
        return LoginService.getParamFromQueryString(this.redirectParamName)
    }

    private static getParamFromQueryString(param: string): string {
        const urlParams = new URLSearchParams(window.location.search)
        return urlParams.get(param)
    }
}
